import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Button, Row, Col, Modal } from 'react-bootstrap';
import axiosOrganizationInstance from './AxiosOrganizationInstance';
// import FeaturesManager from '../components/FeaturesManager';
import HtmlEditor from '../components/HtmlEditor';
import OrganizationFeatures from './OrganizationFeatures';
import {
    getStoredOrganizationId,
    getStoredUserId,
    setStoredOrganizationId,
} from './organizationStorage';
import { formatMobileDisplayOrFallback } from '../restaurant/formatDisplay';
import './css/school-list.css'
import './css/checkbox.css'

function OrganizationList() {
    const [organizationName,setorganizationName] = useState("");
    const [organizationCode, setorganizationCode] = useState("");
    const [admissions, setAdmissions] = useState("");
    const [specialization, setSpecialization] = useState("");
    const [offers, setOffers] = useState("");
    const [features, setFeatures] = useState("");
    const [originalFeaturesData, setOriginalFeaturesData] = useState([]); // Store original features with IDs
    const [phoneNumber, setPhoneNumber] = useState("");
    const [email, setEmail] = useState("");
    const [notes, setNotes] = useState("");
    const [status, setStatus] = useState("");
    const [contactName, setContactName] = useState("");
    const [createdById, setCreatedById] = useState("");
    const [password, setPassword] = useState("");
    const [editMode, setEditMode] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [organizationId, setorganizationId] = useState(null);
    const lockedOrgIdentityRef = useRef({ name: '', code: '' });

    const navigate= useNavigate()

    // Token validation and redirection
    useEffect(() => {
        const checkToken = () => {
            const token = localStorage.getItem("Token");
            if (!token) {
                navigate("/organization/signin");
            }
        };

        checkToken();
        const intervalId = setInterval(checkToken, 200000); // Check token status every 5 minutes
        return () => clearInterval(intervalId);
    }, [navigate]);

    useEffect(() => {
        const fetchorganization = async () => {
          setLoading(true);
          setError(null);

          try {
            const token = localStorage.getItem("Token");
            let orgId = getStoredOrganizationId();
            const userId = getStoredUserId();

            if (!orgId && userId) {
              const linkResponse = await axiosOrganizationInstance.get(
                `/organization/user/${userId}/organization`,
                {
                  headers: {
                    Authorization: `Bearer ${token}`,
                  },
                }
              );
              orgId = String(linkResponse.data.organizationId);
              setStoredOrganizationId(orgId);
            }

            if (!orgId) {
              setorganizationId(null);
              return;
            }

            setorganizationId(orgId);

            const response = await axiosOrganizationInstance.get(`/organization/${orgId}`, {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            });

            const organization = response.data;

            if (organization) {
              const name = organization.organizationName || "N/A";
              const code = organization.organizationCode || "N/A";
              lockedOrgIdentityRef.current = { name, code };
              setorganizationCode(code);
              setorganizationName(name);
              setAdmissions(organization.admissions || "N/A");
              setSpecialization(organization.specialization || "N/A");
              setOffers(organization.offers || "N/A");
              setPhoneNumber(organization.phoneNumber || "N/A");
              setEmail(organization.email || "N/A");
              setNotes(organization.notes || "N/A");
              setStatus(organization.status || "N/A");
              setContactName(organization.contactName || "N/A");
              setCreatedById(organization.createdById || "N/A");
              setPassword(organization.password || "N/A");
            } else {
              setError("No organization data found.");
            }
          } catch (error) {
            console.error("Error fetching organization details:", error);
            if (error.response?.status === 404) {
              setorganizationId(null);
              setError(null);
            } else {
              setError("Failed to fetch organization details.");
            }
          } finally {
            setLoading(false);
          }
        };

        fetchorganization();
      }, []);

    // Fetch features from API
    useEffect(() => {
        const fetchFeatures = async () => {
            const organizationId = getStoredOrganizationId();
            if (!organizationId) {
                return;
            }

            try {
                const token = localStorage.getItem("Token");
                const response = await axiosOrganizationInstance.get(
                    `/organization/${organizationId}/features`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );
                
                const featuresData = response.data || [];
                // Store original features data with IDs for PUT requests
                setOriginalFeaturesData(featuresData);
                
                // Convert grouped JSON format to HTML format
                // API returns: [{category: "Sports", features: [{featureId, featureName}]}]
                if (featuresData.length > 0) {
                    const htmlContent = featuresData.map(categoryGroup => {
                        const categoryName = categoryGroup.category;
                        const featuresList = categoryGroup.features.map(feature => 
                            `<p data-feature-id="${feature.featureId || ''}">${feature.featureName}</p>`
                        ).join('');
                        return `<p><strong>${categoryName}:</strong></p>${featuresList}`;
                    }).join('');
                    setFeatures(htmlContent);
                } else {
                    setFeatures("");
                }
            } catch (error) {
                console.error("Error fetching features:", error);
                setFeatures("");
            }
        };

        if (organizationId) {
            fetchFeatures();
        }
    }, [organizationId]);


    const handleSubmit = async (e) => {
        e.preventDefault();

        const obj = {
            organizationCode: lockedOrgIdentityRef.current.code || organizationCode,
            organizationName: lockedOrgIdentityRef.current.name || organizationName,
            admissions,
            specialization,
            offers,
            // features removed - handled separately via API
            phoneNumber,
            email,
            notes,
            status,
            contactName,
            createdById,
            password
        };

        try {
            const organizationId = getStoredOrganizationId();
            if (editMode) {
                // eslint-disable-next-line
                const token=localStorage.getItem('Token')
                // eslint-disable-next-line
                const response = await axiosOrganizationInstance.put(`/organization/${organizationId}`, obj, {
                    headers: {
                        'Authorization': `Bearer ${token}`, // Set the token in the 'Authorization' header
                    }
                });
                
                // Save features separately using the API
                if (features && features.trim()) {
                    try {
                        // Convert HTML to JSON array format and handle PUT/POST
                        const parser = new DOMParser();
                        const doc = parser.parseFromString(features, 'text/html');
                        const paragraphs = doc.querySelectorAll('p');
                        const featuresToUpdate = []; // Features with IDs (PUT)
                        const featuresToCreate = []; // New features (POST)
                        let currentCategory = null;
                        
                        paragraphs.forEach(p => {
                            const text = p.textContent || p.innerText || '';
                            const featureId = p.getAttribute('data-feature-id');
                            
                            // Check if this is a category line (has bold text)
                            if (p.querySelector('strong')) {
                                const categoryMatch = text.match(/^(.+?):/);
                                if (categoryMatch) {
                                    currentCategory = categoryMatch[1].trim();
                                }
                            } else if (text.trim() && currentCategory) {
                                // This is a feature under the current category
                                const featureData = {
                                    category: currentCategory,
                                    featureName: text.trim()
                                };
                                
                                if (featureId) {
                                    // Existing feature - use PUT
                                    featuresToUpdate.push({
                                        featureId: featureId,
                                        ...featureData
                                    });
                                } else {
                                    // New feature - use POST
                                    featuresToCreate.push(featureData);
                                }
                            } else if (text.trim()) {
                                // Try to parse as "Category: Feature Name" format
                                const match = text.match(/^(.+?):\s*(.+)$/);
                                if (match) {
                                    const featureData = {
                                        category: match[1].trim(),
                                        featureName: match[2].trim()
                                    };
                                    
                                    if (featureId) {
                                        featuresToUpdate.push({
                                            featureId: featureId,
                                            ...featureData
                                        });
                                    } else {
                                        featuresToCreate.push(featureData);
                                    }
                                } else if (text.trim()) {
                                    // No category found, use default
                                    const featureData = {
                                        category: 'General',
                                        featureName: text.trim()
                                    };
                                    
                                    if (featureId) {
                                        featuresToUpdate.push({
                                            featureId: featureId,
                                            ...featureData
                                        });
                                    } else {
                                        featuresToCreate.push(featureData);
                                    }
                                }
                            }
                        });

                        // Update existing features using PUT
                        for (const feature of featuresToUpdate) {
                            try {
                                await axiosOrganizationInstance.put(
                                    `/organization/features/${feature.featureId}`,
                                    {
                                        category: feature.category,
                                        featureName: feature.featureName
                                    },
                                    {
                                        headers: {
                                            Authorization: `Bearer ${token}`,
                                            'Content-Type': 'application/json',
                                        },
                                    }
                                );
                                console.log(`Feature ${feature.featureId} updated successfully`);
                            } catch (error) {
                                console.error(`Error updating feature ${feature.featureId}:`, error);
                            }
                        }

                        // Create new features using POST
                        if (featuresToCreate.length > 0) {
                            const featuresResponse = await axiosOrganizationInstance.post(
                                `/organization/${organizationId}/features`,
                                featuresToCreate,
                                {
                                    headers: {
                                        Authorization: `Bearer ${token}`,
                                        'Content-Type': 'application/json',
                                    },
                                }
                            );
                            console.log('New features created successfully:', featuresResponse.data);
                        }
                    } catch (error) {
                        console.error("Error saving features:", error);
                        // Don't block the main save if features save fails
                    }
                }

                alert('organization updated');
                setEditMode(false);
            } else {
                // const token=localStorage.getItem('Token')
                const response = await axiosOrganizationInstance.post('/organization', obj);
                alert('organization added: ' + JSON.stringify(response.data));
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Failed to save organization details.');
        }
    };

    const handleDelete = async () => {
        // const storedId = localStorage.removeItem('organizationId');
        // if (window.confirm("Are you sure you want to delete this organization?")) {
            try {

                const token=localStorage.getItem('Token')
                const organizationId = getStoredOrganizationId();
                await axiosOrganizationInstance.delete(`/organization/${organizationId}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`, // Set the token in the 'Authorization' header
                    },
                });
                alert('organization deleted');
                resetForm();
                navigate("/organization/signin");
            } catch (error) {
                console.error('Error:', error);
                alert('Failed to delete organization.');
            }
        // }
    };

    const resetForm = () => {
        setorganizationCode("");
        setorganizationName("");
        setAdmissions("");
        setSpecialization("");
        setOffers("");
        setFeatures("");
        setPhoneNumber("");
        setEmail("");
        setNotes("");
        setStatus("");
        setContactName("");
        setCreatedById("");
        setPassword("");
        setEditMode(false);
    };

    return (
        <div className="mt-5 organization-tab-panel">
            {loading && <p>Loading...</p>}
            {error && <p className="text-danger">{error}</p>}
            {!loading && !error && (
                <>
                    {organizationId === null ? (
                        <div style={{ textAlign: "center", marginTop: "20px" }}>
                            <p>No organization linked to this user.</p>
                        </div>
                    ) : (
                        <>
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    borderBottom: "1px solid #D1D1D6",
                                    marginBottom: "10px",
                                    paddingLeft:'30px'
                                }}
                            >
                                <p style={{ fontSize: "24px", fontWeight: "700",color:'black' }}>{organizationName || "N/A"}</p>
                                {!editMode && (
                                    <div
                                        style={{
                                            display: "flex",
                                            justifyContent: "flex-end",
                                            
                                        }}
                                    >
                                        <Button
                                            variant="outline-dark"
                                            onClick={() => setEditMode(true)}
                                            className="me-2"
                                            disabled={status === "INACTIVE"}
                                            style={{
                                                width: "90px",
                                                backgroundColor: status === "INACTIVE" ? "#e9ecef" : "white",
                                                border: "1px solid #017EF3",
                                                color: status === "INACTIVE" ? "#6c757d" : "#017EF3",
                                                cursor: status === "INACTIVE" ? "not-allowed" : "pointer",
                                            }}
                                        >
                                            Edit
                                        </Button>
                                        <Button
                                            variant="outline-dark"
                                            onClick={() => setShowConfirmation(true)}
                                            disabled={status === "INACTIVE"}
                                            style={{
                                                width: "90px",
                                                backgroundColor: status === "INACTIVE" ? "#e9ecef" : "white",
                                                border: "1px solid #017EF3",
                                                color: status === "INACTIVE" ? "#6c757d" : "#017EF3",
                                                cursor: status === "INACTIVE" ? "not-allowed" : "pointer",
                                            }}
                                        >
                                            Delete
                                        </Button>
                                    </div>
                                )}
                            </div>
    
                            {editMode ? (
                                <div style={{marginLeft:'30px'}}>
                                    <Form onSubmit={handleSubmit} >
                                    <Row style={{ marginBottom: "10px" }}>
                                    <Col md={4}>
                                            <Form.Group>
                                                <div style={{ display: "flex", alignItems: "flex-start" }}>
                                                    <Form.Label
                                                        style={{
                                                            fontSize: "14px",
                                                            fontWeight: "700",
                                                            lineHeight: "20px",
                                                            color: "#323E4F",
                                                        }}
                                                    >
                                                        organization Name
                                                    </Form.Label>
                                                </div>
                                                <Form.Control
                                                    type="text"
                                                    value={organizationName}
                                                    readOnly
                                                    disabled
                                                    placeholder="Organization name"
                                                    title="Organization name cannot be changed after registration"
                                                    style={{
                                                        fontSize: "14px",
                                                        fontWeight: "400",
                                                        lineHeight: "20px",
                                                        color: "#697483",
                                                        backgroundColor: "#f0f2f5",
                                                        cursor: "not-allowed",
                                                    }}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group>
                                                <div style={{ display: "flex", alignItems: "flex-start" }}>
                                                    <Form.Label
                                                        style={{
                                                            fontSize: "14px",
                                                            fontWeight: "700",
                                                            lineHeight: "20px",
                                                            color: "#323E4F",
                                                        }}
                                                    >
                                                        Registration Number
                                                    </Form.Label>
                                                </div>
                                                <Form.Control
                                                    type="text"
                                                    value={organizationCode}
                                                    readOnly
                                                    disabled
                                                    placeholder="Registration number"
                                                    title="Registration number cannot be changed after registration"
                                                    style={{
                                                        fontSize: "14px",
                                                        fontWeight: "400",
                                                        lineHeight: "20px",
                                                        color: "#697483",
                                                        backgroundColor: "#f0f2f5",
                                                        cursor: "not-allowed",
                                                    }}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group>
                                                <div style={{ display: "flex", alignItems: "flex-start" }}>
                                                    <Form.Label
                                                        style={{
                                                            fontSize: "14px",
                                                            fontWeight: "700",
                                                            lineHeight: "20px",
                                                            color: "#323E4F",
                                                        }}
                                                    >
                                                        Contact Number
                                                    </Form.Label>
                                                </div>
                                                <Form.Control
                                                    type="text"
                                                    value={phoneNumber}
                                                    onChange={(e) => setPhoneNumber(e.target.value)}
                                                    placeholder="Enter contact number"
                                                    disabled={status === "INACTIVE"}
                                                    style={{
                                                        fontSize: "14px",
                                                        fontWeight: "400",
                                                        lineHeight: "20px",
                                                        color: "#697483",
                                                    }}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    <Row style={{ marginBottom: "10px" }}>
                                        <Col md={4}>
                                            <Form.Group>
                                                <div style={{ display: "flex", alignItems: "flex-start" }}>
                                                    <Form.Label
                                                        style={{
                                                            fontSize: "14px",
                                                            fontWeight: "700",
                                                            lineHeight: "20px",
                                                            color: "#323E4F",
                                                        }}
                                                    >
                                                        Contact Email
                                                    </Form.Label>
                                                </div>
                                                <Form.Control
                                                    type="email"
                                                    value={email}
                                                    onChange={(e) => setEmail(e.target.value)}
                                                    placeholder="Enter contact email"
                                                    disabled={status === "INACTIVE"}
                                                    style={{
                                                        fontSize: "14px",
                                                        fontWeight: "400",
                                                        lineHeight: "20px",
                                                        color: "#697483",
                                                    }}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group>
                                                <div style={{ display: "flex", alignItems: "flex-start" }}>
                                                    <Form.Label
                                                        style={{
                                                            fontSize: "14px",
                                                            fontWeight: "700",
                                                            lineHeight: "20px",
                                                            color: "#323E4F",
                                                        }}
                                                    >
                                                        Admissions
                                                    </Form.Label>
                                                </div>
                                                <Form.Check
                                                    type="checkbox"
                                                    checked={admissions === "open"}
                                                    onChange={(e) => setAdmissions(e.target.checked ? "open" : "closed")}
                                                    placeholder="Enter Open or Closed"
                                                    className='custom-checkbox'
                                                    disabled={status === "INACTIVE"}
                                                />
                                            </Form.Group>
                                        </Col>


                                        <Col md={4}>
                                            <Form.Group>
                                                <div style={{ display: "flex", alignItems: "flex-start" }}>
                                                    <Form.Label
                                                        style={{
                                                            fontSize: "14px",
                                                            fontWeight: "700",
                                                            lineHeight: "20px",
                                                            color: "#323E4F",
                                                        }}
                                                    >
                                                        Specialization
                                                    </Form.Label>
                                                </div>
                                                <Form.Control
                                                    type="text"
                                                    value={specialization}
                                                    onChange={(e) => setSpecialization(e.target.value)}
                                                    placeholder="Enter School Specialization"
                                                    disabled={status === "INACTIVE"}
                                                    style={{
                                                        fontSize: "14px",
                                                        fontWeight: "400",
                                                        lineHeight: "20px",
                                                        color: "#697483",
                                                    }}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group>
                                                <div style={{ display: "flex", alignItems: "flex-start" }}>
                                                    <Form.Label
                                                        style={{
                                                            fontSize: "14px",
                                                            fontWeight: "700",
                                                            lineHeight: "20px",
                                                            color: "#323E4F",
                                                        }}
                                                    >
                                                        Notes
                                                    </Form.Label>
                                                </div>
                                                <Form.Control
                                                    as="textarea"
                                                    rows={3}
                                                    value={notes}
                                                    onChange={(e) => setNotes(e.target.value)}
                                                    placeholder="Enter notes"
                                                    disabled={status === "INACTIVE"}
                                                    style={{
                                                        fontSize: "14px",
                                                        fontWeight: "400",
                                                        lineHeight: "20px",
                                                        color: "#697483",
                                                    }}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group>
                                                <div style={{ display: "flex", alignItems: "flex-start" }}>
                                                    <Form.Label
                                                        style={{
                                                            fontSize: "14px",
                                                            fontWeight: "700",
                                                            lineHeight: "20px",
                                                            color: "#323E4F",
                                                        }}
                                                    >
                                                        Offers
                                                    </Form.Label>
                                                </div>
                                                <Form.Control
                                                    as="textarea"
                                                    rows={3}
                                                    value={offers}
                                                    onChange={(e) => setOffers(e.target.value)}
                                                    placeholder="Enter Offers"
                                                    disabled={status === "INACTIVE"}
                                                    style={{
                                                        fontSize: "14px",
                                                        fontWeight: "400",
                                                        lineHeight: "20px",
                                                        color: "#697483",
                                                    }}
                                                />
                                            </Form.Group>
                                        </Col>
                                       

                                    </Row>
                                    <div style={{ display: "flex", justifyContent: "flex-end" }}>
                                        <Button
                                            variant="outline-dark"
                                            type="submit"
                                            disabled={status === "INACTIVE"}
                                            style={{
                                                width: "90px",
                                                backgroundColor: status === "INACTIVE" ? "#e9ecef" : "white",
                                                border: "1px solid #017EF3",
                                                color: status === "INACTIVE" ? "#6c757d" : "#017EF3",
                                                cursor: status === "INACTIVE" ? "not-allowed" : "pointer",
                                            }}
                                        >
                                            Save
                                        </Button>
                                        <Button
                                            variant="outline-dark"
                                            onClick={() => setEditMode(false)}
                                            className="ms-2"
                                            disabled={status === "INACTIVE"}
                                            style={{
                                                width: "90px",
                                                backgroundColor: status === "INACTIVE" ? "#e9ecef" : "white",
                                                border: "1px solid #017EF3",
                                                color: status === "INACTIVE" ? "#6c757d" : "#017EF3",
                                                cursor: status === "INACTIVE" ? "not-allowed" : "pointer",
                                            }}
                                        >
                                            Cancel
                                        </Button>
                                    </div>
                                </Form>
                                    </div>
                                
                            ) : (
                                <>
                                <div className="row org-details">
                                {/* First Row */}
                                <div className="col-md-4">
                                    <div>
                                        <span style={{ fontSize: "14px", fontWeight: "bold", color: "#445164" }}>
                                            Registration Number:{" "}
                                        </span>
                                        <span style={{ fontSize: "14px", color: "#1B2533" }}>
                                            {organizationCode}
                                        </span>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div>
                                        <span style={{ fontSize: "14px", fontWeight: "bold", color: "#445164" }}>
                                            Contact Number:{" "}
                                        </span>
                                        <span style={{ fontSize: "14px", color: "#1B2533" }}>
                                            {formatMobileDisplayOrFallback(phoneNumber, 'N/A')}
                                        </span>
                                    </div>
                                </div>

                                <div className="col-md-4">
                                    <div>
                                        <span style={{ fontSize: "14px", fontWeight: "bold", color: "#445164" }}>
                                            Email:{" "}
                                        </span>
                                        <span style={{ fontSize: "14px", color: "#1B2533" }}>
                                            {email}
                                        </span>
                                    </div>
                                </div>
                            
                                {/* Second Row */}
                                <div className="w-100 mt-3"></div> {/* Break to next row */}
                                <div className="col-md-4">
                                    <div>
                                        <span style={{ fontSize: "14px", fontWeight: "bold", color: "#445164" }}>
                                            Admissions:{" "}
                                        </span>
                                        <span style={{ fontSize: "14px", color: "#1B2533" }}>
                                            {admissions}
                                        </span>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div>
                                        <span style={{ fontSize: "14px", fontWeight: "bold", color: "#445164" }}>
                                            Specialization:{" "}
                                        </span>
                                        <span style={{ fontSize: "14px", color: "#1B2533" }}>
                                            {specialization}
                                        </span>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div>
                                        <span style={{ fontSize: "14px", fontWeight: "bold", color: "#445164" }}>
                                            Notes:{" "}
                                        </span>
                                        <span style={{ fontSize: "14px", color: "#1B2533" }}>
                                            {notes}
                                        </span>
                                    </div>
                                </div>
                            
                                {/* Third Row */}
                                <div className="w-100 mt-3"></div> {/* Break to next row */}
                                <div className="col-md-4">
                                    <div>
                                        <span style={{ fontSize: "14px", fontWeight: "bold", color: "#445164" }}>
                                            Offers:{" "}
                                        </span>
                                        <span style={{ fontSize: "14px", color: "#1B2533" }}>
                                            {offers}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Features Table Section */}
                            <OrganizationFeatures 
                                organizationId={organizationId} 
                                status={status}
                                onFeaturesUpdate={setFeatures}
                            />
                                </>
                            )}
                        </>
                    )}
                </>
            )}
    
            <Modal show={showConfirmation} onHide={() => setShowConfirmation(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Deletion</Modal.Title>
                </Modal.Header>
                <Modal.Body>Are you sure you want to delete this organization?</Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="outline-dark"
                        onClick={() => setShowConfirmation(false)}
                        style={{
                            width: "90px",
                            backgroundColor: "white",
                            border: "1px solid #017EF3",
                            color: "#017EF3",
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="outline-dark"
                        onClick={handleDelete}
                        disabled={status === "INACTIVE"}
                        style={{
                            width: "90px",
                            backgroundColor: status === "INACTIVE" ? "#e9ecef" : "white",
                            border: "1px solid #017EF3",
                            color: status === "INACTIVE" ? "#6c757d" : "#017EF3",
                            cursor: status === "INACTIVE" ? "not-allowed" : "pointer",
                        }}
                    >
                        Confirm
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}    

export default OrganizationList;
